// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

export interface ChipProps {
  icon?: LucideIcon
  tone?: "neutral" | "accent"
  children: ReactNode
  className?: string
}

/**
 * Compact stat/metadata pill — the fix for owner item 5's "bulky texts, not
 * styled": kcal/macros/time/cost render as small rounded chips instead of a
 * dot-separated text wall. `tone="accent"` is reserved for the single most
 * important figure per card (kcal) so the eye has one clear anchor rather
 * than every number competing at the same weight.
 */
export function Chip({ icon: Icon, tone = "neutral", children, className }: ChipProps) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-bb-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${
        tone === "accent" ? "bg-green/10 text-green" : "border border-line bg-cream text-ink-2"
      } ${className ?? ""}`}
    >
      {Icon && <Icon className="size-3.5 shrink-0" aria-hidden="true" />}
      {children}
    </span>
  )
}
